import { InventoryItemDataService } from './../../../sell-shared/services/inventory-item-data.service';
import { Component, ElementRef, Input, OnInit, ViewChild, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AnalyticsService, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { InventoryItemDetail, InventoryItem, SellerOfferAction, SellErrors, SellerOfferDto, multiLanguageAlphaNumericRegexHelper } from 'remarketing-angular-library';
import { takeUntil } from 'rxjs/operators';
import { get } from 'lodash';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { OfferActionInterface } from '../../interfaces/offer-action.interface';
import { SellErrorsLocal } from '../../../../helpers/sell-errors-local';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  CharacterCountComponent,
  CharacterCountDirective,
  DatalistItemComponent
} from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';
import { NgxMaskModule } from 'ngx-mask';


@Component({
    selector: 'lib-revive',
    templateUrl: './revive.component.html',
    styleUrls: ['./revive.component.scss'],
    imports: [
        TranslocoDirective,
        DatalistItemComponent,
        ReactiveFormsModule,
        CharacterCountComponent,
        CommonModule,
        CharacterCountDirective,
        NgxMaskModule
    ]
})
export class ReviveComponent implements OnInit, OnDestroy {
  static invalidReviveAmountErrorMessage = null;
  public reviveComponent = ReviveComponent;
  private _isVisible = false;
  characterCountData: any;

  @Input() set isVisible(isVisible: boolean) {
    this._isVisible = isVisible;
    if (this._isVisible) {
      setTimeout(() => this.heading.nativeElement.focus());
    }
  }
  get isVisible() {
    return this._isVisible;
  }
  @ViewChild('heading') heading: ElementRef<HTMLHeadingElement>;
  @Input() inventoryItem: InventoryItem;
  @Input() offer: Partial<SellerOfferDto>;
  @Input() tenantName: string;
  destroyed$ = new Subject();
  @Output() cancel = new EventEmitter();
  @Output() action = new EventEmitter<OfferActionInterface>();

  reviveForm: UntypedFormGroup;
  localErrorMessage: string;

  localErrorState$: Observable<{ message: string; offerUuid: string }> = this.inventoryItemDataService.localErrorState;

  reviveAmount: number;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private analyticsService: AnalyticsService,
    private readonly inventoryItemDataService: InventoryItemDataService,
    private readonly translocoService: TranslocoService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {
    ReviveComponent.invalidReviveAmountErrorMessage = SellErrorsLocal.getErrorMessage('REVIVE_OFFER_LESS_OFFER_AMOUNT');
  }

  ngOnInit() {
    this.reviveAmount = this.offer.amount;
    this.reviveForm = this.formBuilder.group({
      offerComment: [''],
      reviveOfferAmount: ['', Validators.required]
    });

    this.localErrorState$.pipe(takeUntil(this.destroyed$)).subscribe(localErrorState => {
      if (localErrorState.offerUuid) {
        localErrorState.offerUuid === this.offer.offerUuid
          ? (this.localErrorMessage = this.determineLocalErrorMessage(localErrorState.message))
          : (this.localErrorMessage = null);
      } else {
        this.localErrorMessage = null;
      }
    });
    this.translocoService.selectTranslateObject('UIComponents').subscribe(translatedObj => {
      this.characterCountData = translatedObj.characterCount;
    });
  }

  determineLocalErrorMessage(errorStateMessage: string): string {
    if (errorStateMessage === SellErrors.MSG_SELL_002) {
      return SellErrorsLocal.REVIVE_OFFER_BUY_NOW_THRESHOLD;
    } else if (errorStateMessage === SellErrors.MSG_SELL_009) {
      return SellErrorsLocal.REVIVE_NOT_ON_BUYER_BIDLIST;
    } else {
      return errorStateMessage;
    }
  }

  get title() {
    return `${this.detail('modelYear')} ${this.detail('make')} ${this.detail('model')} ${this.detail(
      'trim'
    )} VIN ${this.detail('vin')}`;
  }

  detail(key: keyof InventoryItemDetail): string {
    return get(this.itemDetails, key, '');
  }

  get itemDetails(): any | {} {
    return get(this.inventoryItem, 'itemDetail', {});
  }

  get isFrenchCanadian(): boolean {
    return this.translocoService.getActiveLang().includes('fr');
  }

  get(path: string, otherwise: any = 'N/A'): string {
    return get(this.offer, path, otherwise);
  }

  setReviveAmountAndClearFormErrors() {
    this.localErrorMessage = null;
    this.reviveAmount = parseInt(this.reviveForm.get('reviveOfferAmount').value);
  }

  replaceInvalidCharacters(e) {
    e.target.value = multiLanguageAlphaNumericRegexHelper(e.target.value);
  }

  revive() {
    if (this.reviveForm.valid) {
      const payload = {
        offerUuid: this.offer.offerUuid,
        requestedAction: SellerOfferAction.REVIVE,
        comment: this.reviveForm.get('offerComment').value,
        amount: this.reviveAmount
      };
      const analyticsPayload = {
        formValues: {
          name: 'Revive Offer',
          status: null,
          errors: null,
          type: '',
          step: '1',
          stepname: '',
          items: [
            {
              allow: 'yes',
              error: null,
              name: 'Vehicle UUID',
              track: 'yes',
              value: this.inventoryItem ? this.inventoryItem.inventoryItemUuid : null
            },
            {
              allow: 'yes',
              error: null,
              name: 'Buyer Offer',
              track: 'yes',
              value: this.reviveAmount
            }
          ]
        }
      };
      const data = {
        payload,
        analyticsPayload
      };
      this.action.emit(data);
    } else {
      const errors = [];
      if (this.reviveForm.get('reviveOfferAmount').invalid) {
        errors.push('Revive amount is required.');
      }
      const analyticsPayload = {
        formValues: {
          name: 'Revive Offer',
          status: null,
          errors,
          type: '',
          step: '1',
          stepname: '',
          items: [
            {
              allow: 'yes',
              error: null,
              name: 'Vehicle UUID',
              track: 'yes',
              value: this.inventoryItem ? this.inventoryItem.inventoryItemUuid : null
            },
            {
              allow: 'yes',
              error: this.reviveForm.get('reviveOfferAmount').invalid ? 'Revive amount is required.' : null,
              name: 'Buyer Offer',
              track: 'yes',
              value: this.reviveAmount
            }
          ]
        }
      };
      this.analyticsService.trackEndpoint(analyticsPayload);
    }
  }

  formatCurrency(value) {
    return this.currencyFormatService.formatCurrency(value);
  }

  ngOnDestroy(): void {
    this.localErrorMessage = null;
    this.reviveAmount = null;
    this.inventoryItemDataService._localErrorState.next({ message: null, offerUuid: null });
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
